import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { skipToken } from '@reduxjs/toolkit/query';
import useFieldsErrors from 'hooks/useFieldsErrors';
import i18n from 'i18n';

import RegistrationModal from '../../../components/RegistrationModal/RegistrationModal';
import { AppRoutes } from '../../../constants/AppRoutes';
import {
  ATTACHMENTS,
  COMMENT, FILE_NAME, FILE_SIZE,
  FILE_URL, MIME_TYPE, ORIGINAL_FILE_NAME, QUESTIONNAIRE_ID, QUESTIONNAIRE_RECIPIENT_ID, QUESTIONS,
} from '../../../constants/formFields';
import { PRESIGNED_FILE_TYPES } from '../../../constants/interfaces';
import { IRO_QUESTION_TYPES, SURVEY_ANSWER_TYPES } from '../../../constants/materialAssessment';
import useAuthCheck from '../../../hooks/useAuthCheck';
import { useConfirmFileUploadedMutation, useGetQuestionnairePresignedPostMutation } from '../../../store/slices/files/apis/filesApi';
import {
  useIroAnswersCreateMutation,
  useIROLinkCreateMutation,
} from '../../../store/slices/materialAssessment/apis/materialAssessmentApi';
import { selectSurveyToken } from '../../../store/slices/survey/selectors';
import { useUserMeQuery } from '../../../store/slices/user/apis/user';
import { showApiErrorToast, uploadFileToS3 } from '../../../utils/helpers';
import toUploadFileName from '../../../utils/toUploadFileName';
import MAIROQuestionFinishModal from './components/MAIROQuestionFinishModal/MAIROQuestionFinishModal';
import useMAIROQuestionsForm from './hooks/useMAIROQuestionsForm';
import MAIROQuestionView from './MAIROQuestionView';

import type { ReactElement } from 'react';
import type { IQuestionnairePresignedFileRequest } from '../../../store/slices/files/interfaces/IPresignedFileRequest';
import type { IPresignedData } from '../../../store/slices/files/interfaces/IPresignedFileResponse';
import type IIROCreateLinkResponse from '../../../store/slices/materialAssessment/interfaces/IIROCreateLinkResponse';
import type IFileUpload from '../../../store/slices/tasksManagement/interfaces/IFileUpload';
import type { TCreateMAIroQuestionnaireAnswerSchema } from '../../../utils/validators/MAIroValidators';

type TLinkCreateMode = 'anonymous' | 'registered';

function MAIROQuestion(): ReactElement {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [questionData, setQuestionData] = useState<IIROCreateLinkResponse | undefined>(undefined);
  const [currentStep, setCurrentStep] = useState(1);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isReasonModalOpen, setIsReasonModalOpen] = useState(false);
  const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);
  const [linkCreateMode, setLinkCreateMode] = useState<TLinkCreateMode | undefined>(undefined);
  const [attachmentsFiles, setAttachmentsFiles] = useState<IFileUpload[]>([]);
  const [isIroQuestionnaireCompleted, setIroQuestionnaireCompleted] = useState(false);
  const { setFieldsErrors } = useFieldsErrors();
  const isLinkCreateRequestedRef = useRef(false);
  const { isAuthenticated, isLoading: isAuthLoading } = useAuthCheck();
  const [linkCreate, { isLoading: isLoadingData, error: surveyError }] = useIROLinkCreateMutation();
  const [getQuestionnairePresignedPost] = useGetQuestionnairePresignedPostMutation();
  const [confirmFileUploaded] = useConfirmFileUploadedMutation();
  const [createIroAnswers] = useIroAnswersCreateMutation();

  const [hideThirdStep, setHideThirdStep] = useState(false);

  const surveyToken = searchParams.get('token') || '';
  const storedSurveyToken = useSelector(selectSurveyToken);
  const isRegistrationFlowReturned = storedSurveyToken?.token === surveyToken
    && storedSurveyToken.surveyType === SURVEY_ANSWER_TYPES.IRO;

  const { data: userData, isLoading: isUserDataLoading } = useUserMeQuery(isAuthenticated ? undefined : skipToken);
  const respondentId = userData?.data?.id;
  const respondentCompanyId = userData?.data?.companyId;
  const shouldWaitForUserData = isAuthenticated && (isUserDataLoading || !respondentId);

  const {
    control,
    handleSubmit,
    setError,
    reset,
    setValue,
    watch,
  } = useMAIROQuestionsForm();

  const fields = useWatch({ control });

  const initializeFormData = useCallback((responseData: IIROCreateLinkResponse) => ({
    [QUESTIONNAIRE_ID]: responseData.id,
    [QUESTIONNAIRE_RECIPIENT_ID]: responseData.questionnaireRecipientId,
    [COMMENT]: responseData.comment || '',
    [QUESTIONS]: responseData.questions?.map((question) => {
      const existingAnswer = question.answer;
      const isRateQuestion = question.questionType === IRO_QUESTION_TYPES.RATE;

      return {
        questionId: question.id,
        answer: {
          ...(existingAnswer?.id && { answerId: existingAnswer.id }),
          ...(isRateQuestion && { rate: existingAnswer?.rate || 4 }),
          ...(!isRateQuestion && {
            selectedOptionId:
              existingAnswer?.selectedOptionId
                ? Number(existingAnswer?.selectedOptionId)
                : Number(question.options?.[0]?.id),
          }),
          ...(existingAnswer?.rejectReason && { rejectReason: existingAnswer.rejectReason }),
        },
      };
    }) || [],
  }), []);

  useEffect(() => {
    isLinkCreateRequestedRef.current = false;
    setQuestionData(undefined);
    setLinkCreateMode(undefined);
    setIsRegisterModalOpen(false);
  }, [surveyToken]);

  useEffect(() => {
    if (!surveyToken || isAuthLoading || questionData || linkCreateMode) {
      return;
    }

    if (isRegistrationFlowReturned && isAuthenticated) {
      if (shouldWaitForUserData) {
        return;
      }

      setIsRegisterModalOpen(false);
      setLinkCreateMode('registered');
      return;
    }

    setIsRegisterModalOpen(true);
  }, [
    isAuthLoading,
    isAuthenticated,
    isRegistrationFlowReturned,
    linkCreateMode,
    questionData,
    shouldWaitForUserData,
    surveyToken,
  ]);

  useEffect(() => {
    if (
      !surveyToken
      || questionData
      || isLoadingData
      || !linkCreateMode
      || isLinkCreateRequestedRef.current
      || (linkCreateMode === 'registered' && (!isAuthenticated || shouldWaitForUserData || !respondentId))
    ) {
      return;
    }

    isLinkCreateRequestedRef.current = true;

    linkCreate({
      surveyToken,
      ...(linkCreateMode === 'registered' && respondentId ? { respondentId } : {}),
    })
      .unwrap()
      .then((response) => {
        const responseData = response.data;
        setQuestionData(responseData);
        reset(initializeFormData(responseData));
      })
      .catch(() => {
        isLinkCreateRequestedRef.current = false;
        toast.error(i18n.t('surveyAnswer.failedToLoadSurvey', 'Failed to load survey data'));
      });
  }, [
    initializeFormData,
    isAuthenticated,
    isLoadingData,
    linkCreate,
    linkCreateMode,
    questionData,
    respondentId,
    reset,
    shouldWaitForUserData,
    surveyToken,
  ]);

  const uploadFile = useCallback((
    file: File | string,
    fileType: IQuestionnairePresignedFileRequest['fileType'],
  ) => {
    if (typeof file !== 'object') {
      return Promise.resolve(file);
    }

    const handleFileAfterUpload = (presignedData: IPresignedData) => {
      const fileData = {
        fileName: file.name,
        originalFileName: file.name,
        mimeType: file.type,
        fileSize: file.size,
        fileUrl: presignedData.fields.Url,
      };
      setAttachmentsFiles((prev) => [...prev, fileData]);
      const attachFiles = watch(ATTACHMENTS) || [];
      setValue(ATTACHMENTS, [...attachFiles, {
        [FILE_NAME]: file.name,
        [ORIGINAL_FILE_NAME]: file.name,
        [MIME_TYPE]: file.type,
        [FILE_SIZE]: file.size,
        [FILE_URL]: presignedData.fields.Url,
      }]);
    };

    // The file belongs to the company that sent the questionnaire. The confirm route only finds files
    // in the user's own company, so anonymous and other-company respondents rely on the backend check.
    const canConfirmUpload = (presignedData: IPresignedData) => isAuthenticated
      && typeof respondentCompanyId === 'number'
      && presignedData.file.companyId === respondentCompanyId;

    return getQuestionnairePresignedPost({ fileType, token: surveyToken, originalName: toUploadFileName(file.name) })
      .unwrap()
      .then(({ data }) => uploadFileToS3(file, data)
        .then((isUploaded) => {
          if (isUploaded && canConfirmUpload(data)) {
            confirmFileUploaded({ id: data.file.id });
          }
          handleFileAfterUpload(data);
        }))
      .catch((error) => {
        showApiErrorToast(error, i18n.t('notifications.somethingWentWrong'));
        return '';
      });
  }, [
    confirmFileUploaded,
    getQuestionnairePresignedPost,
    isAuthenticated,
    respondentCompanyId,
    setValue,
    surveyToken,
    watch,
  ]);

  const handleFileUpload = useCallback((file: File) => {
    uploadFile(file, PRESIGNED_FILE_TYPES.questionnaire_attachment);
  }, [uploadFile]);

  const handleAttachmentsChange = useCallback((newAttachments: IFileUpload[]) => {
    setAttachmentsFiles(newAttachments);
  }, []);

  const handleFinish = handleSubmit((values) => {
    const step3QuestionIds = new Set(
      questionData?.questions?.filter((q) => Number(q.step) === 3).map((q) => q.id) ?? [],
    );
    const isThirdStepHidden = !hideThirdStep;
    const dataToSend = isThirdStepHidden && step3QuestionIds.size > 0
      ? {
        ...values,
        [QUESTIONS]: (values[QUESTIONS] ?? []).filter(
          (q) => q.questionId == null || !step3QuestionIds.has(Number(q.questionId)),
        ),
      }
      : values;
    createIroAnswers({ data: dataToSend, token: surveyToken }).unwrap()
      .then(() => {
        toast.success(i18n.t('notifications.recordHasBeenAdded'));

        if (respondentId) {
          navigate(AppRoutes.launchpad);
        } else {
          setIroQuestionnaireCompleted(true);
        }
      })
      .catch((errors) => {
        setFieldsErrors<TCreateMAIroQuestionnaireAnswerSchema>({ errors, setError });
      });
  }, () => {
    toast.error(i18n.t('surveyAnswer.validationError', 'Please check your answers, some fields are invalid.'));
  });

  if (surveyError) {
    const expirationMessage = i18n.t(
      'errors.linkWithTokenExpired',
      'The link is invalid or has expired. Please request a new link and try again.',
    );
    return <div>{expirationMessage}</div>;
  }

  const handleNextStep = () => {
    if (currentStep === 0) {
      handleSubmit(
        () => {
          setCurrentStep(1);
        },
        () => {
          toast.error(i18n.t('surveyAnswer.validationError', 'Please check your answers, some fields are invalid.'));
        },
      )();
    } else if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCloseModal = () => {
    setIsRegisterModalOpen(false);
    setLinkCreateMode('anonymous');
  };

  const handleOpenReasonModal = (isOpen: boolean) => {
    if (isOpen !== isReasonModalOpen) {
      setIsReasonModalOpen(isOpen);
    }
  };

  const handleOpenFinishModal = (isOpen: boolean) => {
    setIsFinishModalOpen(isOpen);
  };

  const handleHideThirdStep = (isHide: boolean) => {
    setHideThirdStep(isHide);
  };

  const isRegistrationModalVisible = isRegisterModalOpen && !linkCreateMode && !questionData;

  return (
    <>
      {questionData ? (
        <MAIROQuestionView
          data={questionData}
          control={control}
          currentStep={currentStep}
          onNextStep={handleNextStep}
          onPrevStep={handlePrevStep}
          onOpenReasonModal={handleOpenReasonModal}
          handleOpenFinishModal={handleOpenFinishModal}
          setValue={setValue}
          fields={fields}
          isReasonModalOpen={isReasonModalOpen}
          isIroQuestionnaireCompleted={isIroQuestionnaireCompleted}
          hideThirdStep={hideThirdStep}
          handleHideThirdStep={handleHideThirdStep}
        />
      ) : null}
      <RegistrationModal
        open={isRegistrationModalVisible}
        surveyToken={surveyToken}
        surveyType={SURVEY_ANSWER_TYPES.IRO}
        onClose={handleCloseModal}
      />
      <MAIROQuestionFinishModal
        control={control}
        open={isFinishModalOpen}
        onOpenFinishModal={handleOpenFinishModal}
        onFinish={handleFinish}
        handleFileUpload={handleFileUpload}
        attachmentsFiles={attachmentsFiles}
        handleAttachmentsChange={handleAttachmentsChange}
      />
    </>
  );
}

export default MAIROQuestion;
